import { useMemo, useRef, useState } from "react";
import { WORLD_COUNTRY_POINTS, WORLD_COUNTRY_SHAPES } from "../assets/world_countries";
import type { CountryFlightStats } from "./types";
import {
  buildCountryMapEntries,
  buildCountryPointEntries,
  countryFlag,
  countryName,
  flightCountLabel,
} from "./pilot_country_map";

interface TooltipCountry {
  code: string;
  flightCount: number;
}

interface TooltipState {
  country: TooltipCountry;
  left: number;
  top: number;
}

export function PilotCountryWorldMap({ countries }: { countries: CountryFlightStats[] }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const entries = useMemo(
    () => buildCountryMapEntries(WORLD_COUNTRY_SHAPES, countries),
    [countries],
  );
  const pointEntries = useMemo(
    () => buildCountryPointEntries(WORLD_COUNTRY_POINTS, countries),
    [countries],
  );

  const showTooltip = (country: TooltipCountry, clientX: number, clientY: number) => {
    const bounds = containerRef.current?.getBoundingClientRect();
    if (!bounds) return;
    setTooltip({
      country,
      left: Math.min(Math.max(clientX - bounds.left + 8, 8), Math.max(bounds.width - 178, 8)),
      top: Math.min(Math.max(clientY - bounds.top + 8, 8), Math.max(bounds.height - 72, 8)),
    });
  };

  return (
    <div
      ref={containerRef}
      className="relative h-full min-h-[228px] overflow-hidden rounded-lg bg-white px-5 py-4 shadow dark:bg-[#2D3142]"
    >
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <p className="text-xs font-semibold uppercase tracking-wider text-[#EF8354]">Countries flown</p>
        <p className="text-xs text-[#4F5D75] dark:text-[#BFC0C0]">
          {countries.length} countr{countries.length === 1 ? "y" : "ies"}
        </p>
      </div>
      <svg
        viewBox="0 5 360 150"
        role="img"
        aria-label={`World map with flights in ${countries.length} countries`}
        className="h-auto w-full"
        onMouseLeave={() => setTooltip(null)}
      >
        {entries.map((country) => {
          const label = `${countryFlag(country.code)} ${countryName(country.code)}: ${flightCountLabel(country.flightCount)}`;
          return (
            <path
              key={country.code}
              d={country.path}
              fillRule="evenodd"
              aria-label={country.flightCount > 0 ? label : undefined}
              tabIndex={country.flightCount > 0 ? 0 : undefined}
              pointerEvents={country.flightCount > 0 ? undefined : "none"}
              className={country.flightCount > 0
                ? "cursor-help fill-[#EF8354] stroke-white transition-colors hover:fill-[#d96f45] focus:fill-[#d96f45] focus:outline-none dark:stroke-[#2D3142]"
                : "fill-[#E5E7EB] stroke-white transition-colors hover:fill-[#BFC0C0] dark:fill-[#4F5D75]/45 dark:stroke-[#2D3142] dark:hover:fill-[#4F5D75]/75"}
              strokeWidth={0.35}
              onMouseEnter={(event) => showTooltip(country, event.clientX, event.clientY)}
              onMouseMove={(event) => showTooltip(country, event.clientX, event.clientY)}
              onMouseLeave={() => setTooltip(null)}
              onFocus={(event) => {
                const bounds = event.currentTarget.getBoundingClientRect();
                showTooltip(country, bounds.left + bounds.width / 2, bounds.top + bounds.height / 2);
              }}
              onBlur={() => setTooltip(null)}
            >
              {country.flightCount > 0 && <title>{label}</title>}
            </path>
          );
        })}
        {pointEntries.filter((country) => country.flightCount > 0).map((country) => {
          const label = `${countryFlag(country.code)} ${countryName(country.code)}: ${flightCountLabel(country.flightCount)}`;
          return (
            <circle
              key={country.code}
              cx={country.x}
              cy={country.y}
              r={1.8}
              aria-label={label}
              tabIndex={0}
              className="cursor-help fill-[#EF8354] stroke-white transition-colors hover:fill-[#d96f45] focus:fill-[#d96f45] focus:outline-none dark:stroke-[#2D3142]"
              strokeWidth={0.5}
              onMouseEnter={(event) => showTooltip(country, event.clientX, event.clientY)}
              onMouseMove={(event) => showTooltip(country, event.clientX, event.clientY)}
              onMouseLeave={() => setTooltip(null)}
              onFocus={(event) => {
                const bounds = event.currentTarget.getBoundingClientRect();
                showTooltip(country, bounds.left + bounds.width / 2, bounds.top + bounds.height / 2);
              }}
              onBlur={() => setTooltip(null)}
            >
              <title>{label}</title>
            </circle>
          );
        })}
      </svg>
      {tooltip && (
        <div
          role="tooltip"
          className="pointer-events-none absolute z-10 max-w-[170px] rounded-md border border-[#BFC0C0]/30 bg-white px-2.5 py-1.5 text-xs shadow-lg dark:border-[#4F5D75] dark:bg-[#2D3142]"
          style={{ left: tooltip.left, top: tooltip.top }}
        >
          <p className="font-semibold text-[#2D3142] dark:text-white">
            <span className="mr-1.5" aria-hidden="true">{countryFlag(tooltip.country.code)}</span>
            {countryName(tooltip.country.code)}
          </p>
          <p className="text-[#4F5D75] dark:text-[#BFC0C0]">
            {flightCountLabel(tooltip.country.flightCount)}
          </p>
        </div>
      )}
    </div>
  );
}
